import React, { useCallback, useEffect, useMemo, useRef } from "react";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import { ServiceRow } from "../logicBuilder/NewLogicModal";
import ScrollableWrapper from "../common/ScrollableWrapper";
import ServiceEditor from "../logicBuilder/ServiceEditor";
import LabPanelEditor from "../logicBuilder/LabPanelEditor";
import { getPanelQuestions } from "../../utils/labPanelQuestions";
import { useServicesFetcher } from "../common/hooks/useServicesFetcher";

interface PrescriptionBuilderStep2Props {
  serviceRows: ServiceRow[];
  services: any[];
  onServicesDataChange?: (services: any[]) => void;
  labPanels?: any[];
  savedLabPanels?: any[];
  onLabPanelsDataChange?: (labPanels: any[]) => void;
}

const ServiceEditorSkeleton = () => {
  const theme: Theme = useTheme();

  return (
    <div style={styles.scrollableContent(theme)}>
      {[1].map((_, index) => (
        <div key={index}>
          {index > 0 && <div style={styles.serviceSeparator(theme)}></div>}
          <div style={styles.serviceCard()}>
            <div style={styles.skeletonContainer}>
              {/* Service header skeleton */}
              <div style={styles.skeletonServiceTitle(theme)} />
              <div style={styles.skeletonServiceType(theme)} />

              {/* Attributes grid skeleton */}
              <div style={styles.skeletonAttributesGrid}>
                <div style={styles.skeletonField(theme)} />
                <div style={styles.skeletonField(theme)} />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const PrescriptionBuilderStep2 = ({
  serviceRows,
  services,
  onServicesDataChange,
  labPanels = [],
  savedLabPanels = [],
  onLabPanelsDataChange,
}: PrescriptionBuilderStep2Props) => {
  const theme: Theme = useTheme();
  const scrollableRef = useRef<HTMLDivElement>(null);

  // Separate service-only rows from lab panel rows
  const serviceOnlyRows = useMemo(
    () => serviceRows.filter((r) => !r.isLabPanel),
    [serviceRows]
  );
  const labPanelRows = useMemo(
    () => serviceRows.filter((r) => r.isLabPanel),
    [serviceRows]
  );

  const { isLoading, error } = useServicesFetcher({
    serviceRows: serviceOnlyRows,
    services,
    onServicesDataChange,
  });

  // Initialize/sync lab panel configs when lab panel rows change
  useEffect(() => {
    if (labPanelRows.length === 0) {
      if (labPanels.length > 0) {
        onLabPanelsDataChange?.([]);
      }
      return;
    }

    const updatedLabPanels = labPanelRows.map((row) => {
      const existing = labPanels.find(
        (lp: any) => lp.userLabPanelID === row.selectedServiceId
      );
      if (existing) return existing;

      // Prefer the panel object carried on the row (covers custom AND provider
      // panels chosen via the modal); fall back to the saved-panels lookup.
      const panelData =
        row.labPanelData ||
        savedLabPanels.find(
          (p: any) => p.id?.toString() === row.selectedServiceId?.toString()
        );
      return {
        userLabPanelID: row.selectedServiceId,
        panelData: panelData || null,
        startDate: null,
        // The lab's order-entry questions, answered in the editor; the full
        // objects (with `userAnswer`) are sent with the prescription.
        questions: getPanelQuestions(panelData),
      };
    });

    // Only update if changed
    const currentIds = labPanels.map((lp: any) => lp.userLabPanelID).join(",");
    const newIds = updatedLabPanels
      .map((lp: any) => lp.userLabPanelID)
      .join(",");
    if (currentIds !== newIds) {
      onLabPanelsDataChange?.(updatedLabPanels);
    }
  }, [labPanelRows, savedLabPanels]);

  const handleLabPanelConfigChange = useCallback(
    (index: number, updatedConfig: any) => {
      const updated = labPanels.map((lp: any, i: number) =>
        i === index ? updatedConfig : lp
      );
      onLabPanelsDataChange?.(updated);
    },
    [labPanels, onLabPanelsDataChange]
  );

  return (
    <div style={styles.mainContainer}>
      <div style={styles.headerSection}>
        <div style={styles.title(theme)}>Service Values</div>
        <div style={styles.text(theme)}>
          Review and edit the pre-filled values for this service, and set the
          start and end date for the prescription.
        </div>
      </div>
      <div style={styles.contentWithScroll}>
        <ScrollableWrapper ref={scrollableRef}>
          <div>
            {isLoading ? (
              <ServiceEditorSkeleton />
            ) : error ? (
              <div style={styles.errorContainer(theme)}>
                Error loading services: {error}
              </div>
            ) : services.length === 0 && labPanels.length === 0 ? (
              <div style={styles.emptyContainer(theme)}>
                No services selected. Go back to Step 1 to select services.
              </div>
            ) : (
              <div style={styles.scrollableContent(theme)}>
                {services.map((service: any, index: number) => (
                  <div key={service.userServiceID || service.id || index}>
                    {index > 0 && (
                      <div style={styles.serviceSeparator(theme)}></div>
                    )}
                    <div style={styles.serviceCard()}>
                      <ServiceEditor
                        type={"prescriptionBuilder"}
                        service={service}
                        onServiceChange={(updatedService: any) => {
                          const updatedServices = services.map(
                            (s: any, idx: number) =>
                              idx === index ? updatedService : s
                          );
                          onServicesDataChange?.(updatedServices);
                        }}
                        forceSmallScreen={true}
                      />
                    </div>
                  </div>
                ))}
                {/* Lab panel editors */}
                {labPanels.map((lp: any, index: number) => {
                  const showSeparator = services.length > 0 || index > 0;
                  return (
                    <div key={lp.userLabPanelID || index}>
                      {showSeparator && (
                        <div style={styles.serviceSeparator(theme)}></div>
                      )}
                      <div style={styles.serviceCard()}>
                        <LabPanelEditor
                          panelConfig={lp}
                          panelData={lp.panelData}
                          onPanelConfigChange={(updatedConfig: any) =>
                            handleLabPanelConfigChange(index, updatedConfig)
                          }
                          forceSmallScreen={true}
                          type="prescriptionBuilder"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </ScrollableWrapper>
      </div>
    </div>
  );
};

export default PrescriptionBuilderStep2;

const styles = {
  mainContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column" as "column",
    minHeight: 0,
    maxHeight: "100%",
    overflow: "hidden",
  },
  headerSection: {
    marginBottom: "16px",
  },
  contentWithScroll: {
    flex: 1,
    display: "flex",
    flexDirection: "column" as "column",
    minHeight: 0,
    maxHeight: "100%",
    overflow: "hidden",
    marginRight: "-10px",
  },
  scrollableContent: (theme: Theme) => ({
    display: "flex",
    flexDirection: "column" as "column",
    gap: "0px",
    borderRadius: "8px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    backgroundColor: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
    overflow: "hidden",
  }),
  title: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  text: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "17px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    marginTop: "4px",
  }),
  errorContainer: (theme: Theme) => ({
    padding: "16px",
    borderRadius: "8px",
    backgroundColor: theme.colorTokens.P[10],
    color: theme.colorTokens.P[70],
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
  }),
  emptyContainer: (theme: Theme) => ({
    padding: "32px 16px",
    textAlign: "center" as "center",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
  }),
  serviceCard: () => ({
    padding: "0",
  }),
  serviceSeparator: (theme: Theme) => ({
    borderBottom: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    margin: "8px 16px",
  }),
  skeletonContainer: {
    padding: "16px",
    display: "flex",
    flexDirection: "column" as "column",
    gap: "24px",
  },
  skeletonServiceTitle: (theme: Theme) => ({
    height: "20px",
    width: "180px",
    backgroundColor: theme.colorTokens.N[20],
    borderRadius: "4px",
    animation: "pulse 1.5s ease-in-out infinite",
  }),
  skeletonServiceType: (theme: Theme) => ({
    height: "17px",
    width: "120px",
    backgroundColor: theme.colorTokens.N[20],
    borderRadius: "4px",
    animation: "pulse 1.5s ease-in-out infinite",
  }),
  skeletonAttributesGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "16px",
  },
  skeletonField: (theme: Theme) => ({
    height: "56px",
    backgroundColor: theme.colorTokens.N[20],
    borderRadius: "4px",
    animation: "pulse 1.5s ease-in-out infinite",
  }),
};
